import _, { get, isArray, isBoolean, isEmpty, isNaN, isNumber, uniqueId } from 'lodash';
import Cookies from 'js-cookie';
import qs from 'querystring';
import { logger } from './log';
import { BILLING_CYCLE } from '@/common/constants';

export const isEmptyVal = (value) => {
  if (value instanceof Date || isBoolean(value)) {
    return false;
  }

  return isNumber(value) ? isNaN(value) : isEmpty(value);
};

export const billingCycleIsMonthly = (period) => {
  return period === BILLING_CYCLE.MONTHLY;
};

export const billingCycleIsOneMonth = (period) => {
  return period === BILLING_CYCLE.ONE_MONTH;
};

export const billingCycleIsAnnual = (period) => {
  return [BILLING_CYCLE.ANNUAL, BILLING_CYCLE.ANNUAL_TERM_MONTHLY].includes(period);
};

export const billingCycleIsAnnualMonthly = (period) => {
  return period === BILLING_CYCLE.ANNUAL_TERM_MONTHLY;
};

export const billingCycleToValueMap = {
  [BILLING_CYCLE.MONTHLY]: 1,
  [BILLING_CYCLE.QUARTERLY]: 3,
  [BILLING_CYCLE.ANNUAL_TERM_MONTHLY]: 12,
  [BILLING_CYCLE.ANNUAL]: 12,
  [BILLING_CYCLE.TWO_YEARS]: 24,
  [BILLING_CYCLE.THREE_YEARS]: 36,
};
export const getValueByBillingCycle = (period) => {
  return get(billingCycleToValueMap, period, billingCycleToValueMap[BILLING_CYCLE.MONTHLY]);
}

export const safeJSONParse = (jsonString, defaultValue = null) => {
  let json = defaultValue;
  try {
    json = JSON.parse(jsonString);
  } catch (e) {
    logger.warn(e);
  }
  return json;
};

export const isDebug = process.env.NODE_ENV !== 'production';

export const isDevEnv = get(window.newBillingConfig, "isDevEnv", false);

export const getLang = Cookies.get('_zm_lang') || 'en-US'

export const compose = fns => {
  if (fns.length === 0) {
    return args => args;
  }
  if (fns.length === 1) {
    return fns[0];
  }
  return fns.reduce((fn1, fn2) => (...args) => fn1(fn2(...args)));
};

export const getQueryByLocationSearch = () => {
  const searchString = location.search?.replace(/^\?/g, '') || "";
  return qs.parse(searchString) || {};
}

export const noopPromise = () => Promise.resolve();

export const isMobile = () => {
  return /.*(mobile|android|iphone|ipad|phone).*/i.test(navigator.userAgent);
}

export const ensureArray = (val) => (Array.isArray(val) ? val : []);

/**
 * Synchronize existing parameters of browser URL to new URL
 * @param pathname
 * @param queryParams
 * @returns {string|string|*}
 */

export const URL_PARAM = {
  PAGE_TYPE: "pageType",
}

export const updateHrefWithQueryParams = (pathname, queryParams, otherQueryParamInfo = {}) => {
  if (!(isArray(queryParams) && queryParams.length > 0)) {
    return pathname;
  }
  const currentUrl = new URL(window.location.href);
  // Check if pathname includes a domain
  let newUrl;
  let isAbsolute = false;
  try {
    newUrl = new URL(pathname);
    isAbsolute = true;
  } catch (e) {
    newUrl = new URL(pathname, window.location.origin);
  }
  queryParams.forEach(param => {
    if (currentUrl.searchParams.has(param) && !newUrl.searchParams.has(param)) {
      newUrl.searchParams.set(param, currentUrl.searchParams.get(param));
    }
  });

  _.keys(otherQueryParamInfo).forEach((param) => {
    newUrl.searchParams.set(param, _.get(otherQueryParamInfo, param));
  });

  return isAbsolute ? newUrl.href : newUrl.pathname + newUrl.search;
};

let transformElm = null
export function htmlTextToPlainText(htmlText) {
  if (!transformElm) {
    transformElm = document.createElement('div')
  }
  transformElm.innerHTML = htmlText
  const ret = transformElm.innerText
  transformElm.innerHTML = ''
  return ret
}

export const isLastPlanTypeInPlantypeList = (planType, planTypeList = []) => {
  return planTypeList.length === 1 && planTypeList.includes(planType)
}

export const formatSentence = (s) => {
  try {
    let ls = s.toLowerCase();
    return ls.replace(/^\b([a-z])/, function(match) {
      return match.toUpperCase();
    });
  }catch (e) {
    return s
  }
}

/**
 * Add additional query parameters to current URL
 * @param {string} currentUrl - Current URL path (can be relative or absolute)
 * @param {Object} additionalParams - Additional parameters to add
 * @returns {string} Updated URL with additional parameters
 *
 * @example
 * // Relative path
 * addUrlParams('/opc/buy/config?xxx=55', { attachType: 'preview', subType: '300x300' })
 * // Returns: '/opc/buy/config?xxx=55&attachType=preview&subType=300x300'
 *
 * @example
 * // Absolute URL
 * addUrlParams('https://deva.zoomdev.us/opc/buy/config?jointsale=bd_clinical', { attachType: 'preview' })
 * // Returns: 'https://deva.zoomdev.us/opc/buy/config?jointsale=bd_clinical&attachType=preview&subType=300x300'
 */
export const addUrlParams = (currentUrl, additionalParams = {}) => {
  if (!currentUrl || typeof currentUrl !== 'string') {
    return currentUrl;
  }

  // Handle empty additionalParams
  if (!additionalParams || _.keys(additionalParams).length === 0) {
    return currentUrl;
  }

  try {
    // Parse the current URL
    let url;
    let isAbsolute = false;

    try {
      // Try to parse as absolute URL
      url = new URL(currentUrl);
      isAbsolute = true;
    } catch (e) {
      // If parsing fails, treat as relative URL
      url = new URL(currentUrl, window.location.origin);
    }

    // Add additional parameters
    _.keys(additionalParams).forEach(key => {
      const value = additionalParams[key];
      if (value !== null && value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    });

    // Return the appropriate format based on input type
    return isAbsolute ? url.href : url.pathname + url.search;

  } catch (error) {
    console.warn('Failed to parse URL:', currentUrl, error);
    return currentUrl;
  }
};

/**
 * Generate a unique identifier
 * @returns {string} A unique identifier string
 */
export const generateOpcUniqueId = () => {
  return uniqueId('opc-' + Date.now().toString(36) + Math.random().toString(36).slice(2));
};

export { getUrlSearchParams } from './url';

/**
 * Return a copy of the object with all keys removed whose value is null or undefined.
 * @param {Object} obj - Source object
 * @returns {Object} New object with only non-null, non-undefined values
 */
export function cleanObj(obj) {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => value != null));
}